import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'

import { useMayOpen } from '@/features/auth/access'
import { ExportButton } from '@/components/ExportButton'
import { QueryState } from '@/components/QueryState'
import { Alert } from '@/components/ui/Alert'
import { Card, CardHeader } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import {
  ACCOUNT_TYPE_LABELS,
  exportTrialBalance,
  formatMoney,
  formatSide,
  getProfitAndLoss,
  getTrialBalance,
  startOfMonth,
  today,
  type BalanceRow,
} from '@/features/accounting/api'
import { listLocations } from '@/features/orders/api'

const SELECT_CLASS =
  'h-10 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 ' +
  'focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 ' +
  'dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100'

/**
 * A balance is signed against the account's own normal side, so a debit account with a
 * credit balance shows negative. That is the interesting case rather than a rendering
 * problem -- a negative bank balance or a negative revenue account is something to look at.
 */
function Amount({ value }: { value: string }) {
  const negative = Number(value) < 0
  return (
    <span className={negative ? 'tabular-nums text-danger' : 'tabular-nums'}>
      {formatMoney(value)}
    </span>
  )
}

function BalanceTable({ rows }: { rows: BalanceRow[] }) {
  // The ledger sits behind the chart of accounts, which not every reader of the trial
  // balance holds. Without the name it is still the row that matters.
  const mayOpenLedger = useMayOpen('/accounting/ledger/:accountId')
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <caption className="sr-only">Account balances</caption>
        <thead className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:text-slate-400">
          <tr>
            <th className="px-5 py-2 font-medium">Code</th>
            <th className="px-5 py-2 font-medium">Account</th>
            <th className="px-5 py-2 font-medium">Type</th>
            <th className="px-5 py-2 text-right font-medium">Debit</th>
            <th className="px-5 py-2 text-right font-medium">Credit</th>
            <th className="px-5 py-2 text-right font-medium">Balance</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
          {rows.map((row) => (
            <tr key={row.account_id}>
              <td className="px-5 py-2 font-mono text-xs">{row.code}</td>
              <td className="px-5 py-2 font-medium">
                {mayOpenLedger ? (
                  <Link to={`/accounting/ledger/${row.account_id}`} className="hover:underline">
                    {row.name}
                  </Link>
                ) : (
                  row.name
                )}
              </td>
              <td className="px-5 py-2 text-slate-500 dark:text-slate-400">
                {ACCOUNT_TYPE_LABELS[row.type]}
              </td>
              <td className="px-5 py-2 text-right tabular-nums">{formatSide(row.debit)}</td>
              <td className="px-5 py-2 text-right tabular-nums">{formatSide(row.credit)}</td>
              <td className="px-5 py-2 text-right font-medium">
                <Amount value={row.balance} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/**
 * Revenue and expenses over a window, on the same screen as the trial balance.
 *
 * They answer different questions -- "do the books add up" and "did we make money" -- but
 * they are read one after the other, and the second is three lines of derived data rather
 * than a screen of its own.
 */
function ProfitAndLoss() {
  const [start, setStart] = useState(startOfMonth())
  const [end, setEnd] = useState(today())
  const [locationId, setLocationId] = useState('')

  const { data: locationsPage } = useQuery({
    queryKey: ['locations'],
    queryFn: () => listLocations(),
  })
  const locations = locationsPage?.data ?? []

  const { data, isLoading, error } = useQuery({
    queryKey: ['profit-and-loss', start, end, locationId],
    queryFn: () => getProfitAndLoss(start, end, locationId || undefined),
    retry: false,
  })

  const profitable = data ? Number(data.net_profit) >= 0 : true

  return (
    <Card>
      <CardHeader
        title="Profit and loss"
        description="Revenue less expenses over the window. Revenue is shown net of its contra accounts, so a discount reduces it rather than appearing as a cost."
      />
      <div className="flex flex-wrap items-end gap-3 border-b border-slate-200 px-5 py-4 dark:border-slate-800">
        <label className="text-sm">
          <span className="block text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Location
          </span>
          <select
            aria-label="Location"
            className={`mt-1 ${SELECT_CLASS}`}
            value={locationId}
            onChange={(event) => setLocationId(event.target.value)}
          >
            <option value="">Any location</option>
            {locations.map((location) => (
              <option key={location.id} value={location.id}>
                {location.name}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm">
          <span className="block text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
            From
          </span>
          <Input
            type="date"
            className="mt-1 w-auto"
            value={start}
            max={end}
            onChange={(event) => setStart(event.target.value)}
          />
        </label>
        <label className="text-sm">
          <span className="block text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
            To
          </span>
          <Input
            type="date"
            className="mt-1 w-auto"
            value={end}
            min={start}
            onChange={(event) => setEnd(event.target.value)}
          />
        </label>
      </div>

      <QueryState isLoading={isLoading} error={error}>
        {data && (
          <div className="space-y-4 p-5">
            <div className="grid gap-4 tablet:grid-cols-3">
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Revenue
                </p>
                <p className="text-lg font-semibold tabular-nums">
                  {formatMoney(data.total_revenue)}
                </p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Expenses
                </p>
                <p className="text-lg font-semibold tabular-nums">
                  {formatMoney(data.total_expenses)}
                </p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Net {profitable ? 'profit' : 'loss'}
                </p>
                <p
                  className={`text-lg font-semibold tabular-nums ${
                    profitable ? 'text-success' : 'text-danger'
                  }`}
                >
                  {formatMoney(data.net_profit)}
                </p>
              </div>
            </div>

            {data.revenue.length + data.expenses.length === 0 ? (
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Nothing posted to a revenue or expense account in this window.
              </p>
            ) : (
              <div className="-mx-5">
                <BalanceTable rows={[...data.revenue, ...data.expenses]} />
              </div>
            )}
          </div>
        )}
      </QueryState>
    </Card>
  )
}

export function TrialBalance() {
  const [asOf, setAsOf] = useState(today())
  const [locationId, setLocationId] = useState('')

  const { data: locationsPage } = useQuery({
    queryKey: ['locations'],
    queryFn: () => listLocations(),
  })
  const locations = locationsPage?.data ?? []

  const { data, isLoading, error } = useQuery({
    queryKey: ['trial-balance', asOf, locationId],
    queryFn: () => getTrialBalance(asOf, locationId || undefined),
    retry: false,
  })

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Trial balance</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Every account with a movement, as at a date.
          </p>
        </div>
        <label className="text-sm">
          <span className="block text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Location
          </span>
          <select
            aria-label="Location"
            className={`mt-1 ${SELECT_CLASS}`}
            value={locationId}
            onChange={(event) => setLocationId(event.target.value)}
          >
            <option value="">Any location</option>
            {locations.map((location) => (
              <option key={location.id} value={location.id}>
                {location.name}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm">
          <span className="block text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
            As at
          </span>
          <Input
            type="date"
            className="mt-1 w-auto"
            value={asOf}
            onChange={(event) => setAsOf(event.target.value)}
          />
        </label>
        <ExportButton
          filename={`trial_balance_${asOf}`}
          selectClassName={SELECT_CLASS}
          onExport={(format) => exportTrialBalance(format, asOf, locationId || undefined)}
          errorMessage="Could not export the trial balance."
        />
      </header>

      {/* Debits equal credits or the ledger is broken. The database enforces it per entry,
          so this can only fail if something bypassed it -- which is worth shouting about. */}
      {data && !data.balanced && (
        <Alert tone="error" title="The books do not balance">
          Debits total {formatMoney(data.total_debit)} against credits of{' '}
          {formatMoney(data.total_credit)}. Every entry is checked at commit, so this means
          something has written to the ledger outside the application. Stop posting and raise it.
        </Alert>
      )}

      <Card>
        <CardHeader
          title={`Balances to ${new Date(`${asOf}T00:00:00`).toLocaleDateString()}`}
          description="Signed against each account's normal side: a negative revenue balance is a contra account such as discounts."
        />
        <QueryState
          isLoading={isLoading}
          error={error}
          isEmpty={data?.rows.length === 0}
          emptyMessage="Nothing has been posted on or before this date."
        >
          {data && (
            <>
              <BalanceTable rows={data.rows} />
              <div className="flex items-center justify-end gap-8 border-t border-slate-200 px-5 py-3 text-sm font-medium dark:border-slate-800">
                <span className="text-slate-500 dark:text-slate-400">Totals</span>
                <span className="tabular-nums">Dr {formatMoney(data.total_debit)}</span>
                <span className="tabular-nums">Cr {formatMoney(data.total_credit)}</span>
                {data.balanced && (
                  <span className="rounded bg-success/10 px-2 py-0.5 text-xs font-medium text-success">
                    Balanced
                  </span>
                )}
              </div>
            </>
          )}
        </QueryState>
      </Card>

      <ProfitAndLoss />
    </div>
  )
}
